import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { renderAsync as renderDocxAsync } from 'docx-preview';
import { registerTemplatesApi } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  FileText,
  Upload,
  Star,
  Power,
  Trash2,
  Download,
  Pencil,
  ShieldCheck,
  AlertTriangle,
  Code,
  Info,
  RotateCcw,
  Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const formatBytes = (b) => {
  if (!b) return '-';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function RegisterTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', description: '', is_default: false, file: null });
  const [editing, setEditing] = useState(null);
  const [placeholderDialog, setPlaceholderDialog] = useState(null); // {template, found, valid, unknown, supported}
  const [previewDialog, setPreviewDialog] = useState(null); // {template}
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewValidation, setPreviewValidation] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const previewContainerRef = useRef(null);
  const [showHelp, setShowHelp] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const fileRef = useRef(null);

  const handleCheckPlaceholders = async (template) => {
    try {
      const r = await registerTemplatesApi.getPlaceholders(template.id);
      setPlaceholderDialog({ template, ...r.data });
    } catch (e) {
      toast.error('Errore verifica placeholder: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handlePreview = (template) => {
    // Apre il dialog; il render avviene dentro useEffect quando il container è montato
    setPreviewDialog({ template });
    setPreviewLoading(true);
    setPreviewError('');
    setPreviewValidation(null);
  };

  // Effettua il render con docx-preview quando il dialog è aperto e il container è disponibile
  useEffect(() => {
    if (!previewDialog?.template) return;
    const tpl = previewDialog.template;
    let cancelled = false;

    const run = async () => {
      // Attendi che il ref sia popolato (Dialog appena montato)
      let attempts = 0;
      while (!previewContainerRef.current && attempts < 30) {
        await new Promise(r => setTimeout(r, 50));
        attempts++;
      }
      if (cancelled || !previewContainerRef.current) return;
      try {
        // 1) Scarica .docx tramite fetch con ?token=... (evita interceptor axios)
        const url = registerTemplatesApi.getDownloadUrl(tpl.id);
        const resp = await fetch(url, { method: 'GET' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        // 2) Render fedele con docx-preview (mantiene tabelle, header, font, layout)
        previewContainerRef.current.innerHTML = '';
        await renderDocxAsync(blob, previewContainerRef.current, null, {
          className: 'docx-render',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          breakPages: true,
          experimental: true,
        });
        if (cancelled) return;
        // 3) Evidenzia i placeholder «...» nel DOM renderizzato
        highlightPlaceholdersInDom(previewContainerRef.current);
        // 4) Carica validation per badge sintesi
        try {
          const v = await registerTemplatesApi.getPlaceholders(tpl.id);
          if (!cancelled) setPreviewValidation(v.data);
        } catch (_) { /* ignore */ }
      } catch (e) {
        if (!cancelled) {
          setPreviewError(e?.message || 'Errore caricamento anteprima');
          toast.error('Errore anteprima: ' + (e?.message || ''));
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [previewDialog]);

  // Wrappa ogni occorrenza di «...» nei TEXT NODES con un <mark> giallo
  const highlightPlaceholdersInDom = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const re = /«[^»]+»/g;
    const toProcess = [];
    let n;
    while ((n = walker.nextNode())) {
      if (n.nodeValue && re.test(n.nodeValue)) {
        re.lastIndex = 0;
        toProcess.push(n);
      }
    }
    toProcess.forEach((node) => {
      const text = node.nodeValue;
      const frag = document.createDocumentFragment();
      let lastIndex = 0;
      const matches = [...text.matchAll(/«[^»]+»/g)];
      matches.forEach((m) => {
        if (m.index > lastIndex) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex, m.index)));
        }
        const mark = document.createElement('mark');
        mark.style.background = '#fde68a';
        mark.style.border = '1px solid #f59e0b';
        mark.style.borderRadius = '3px';
        mark.style.padding = '1px 4px';
        mark.style.fontFamily = 'monospace';
        mark.style.fontSize = '0.85em';
        mark.textContent = m[0];
        frag.appendChild(mark);
        lastIndex = m.index + m[0].length;
      });
      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      node.parentNode.replaceChild(frag, node);
    });
  };

  const handleOpenHelp = async () => {
    try {
      const r = await registerTemplatesApi.getPlaceholdersCatalog();
      setCatalog(r.data);
      setShowHelp(true);
    } catch (e) {
      toast.error('Errore: ' + e.message);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await registerTemplatesApi.getAll();
      setTemplates(res.data);
    } catch {
      toast.error('Errore caricamento template');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Seleziona un file .docx');
      return;
    }
    if (!uploadForm.name) {
      toast.error('Inserisci un nome per il template');
      return;
    }
    if (!uploadForm.file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Il file deve essere in formato .docx');
      return;
    }
    try {
      setUploading(true);
      await registerTemplatesApi.upload(
        uploadForm.file,
        uploadForm.name,
        uploadForm.description,
        uploadForm.is_default
      );
      toast.success('Template caricato');
      setShowUpload(false);
      setUploadForm({ name: '', description: '', is_default: false, file: null });
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
    }
  };

  const setDefault = async (t) => {
    try {
      await registerTemplatesApi.update(t.id, { is_default: true });
      toast.success(`"${t.name}" è ora il template predefinito`);
      load();
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const toggleActive = async (t) => {
    if (t.is_default && t.is_active) {
      toast.error('Non puoi disattivare il template predefinito. Imposta prima un altro template come predefinito.');
      return;
    }
    try {
      await registerTemplatesApi.update(t.id, { is_active: !t.is_active });
      toast.success(t.is_active ? 'Template disattivato' : 'Template riattivato');
      load();
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const renameTemplate = async () => {
    if (!editing?.name) return;
    try {
      await registerTemplatesApi.update(editing.id, {
        name: editing.name,
        description: editing.description,
      });
      toast.success('Template aggiornato');
      setEditing(null);
      load();
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const deleteTemplate = async (t) => {
    const msg = t.is_builtin
      ? `Eliminare il template predefinito "${t.name}"?\nPotrai ripristinarlo con il pulsante "Ripristina predefiniti".`
      : `Eliminare il template "${t.name}"?\nQuesta azione è irreversibile.`;
    if (!window.confirm(msg)) return;
    try {
      await registerTemplatesApi.delete(t.id);
      toast.success('Template eliminato');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione');
    }
  };

  // v1.3.22: nascondi i template predefiniti/backup (is_builtin) nella gestione:
  // l'utente vuole vedere solo i propri template caricati (con i placeholder).
  const visibleTemplates = templates.filter((t) => !t.is_builtin);

  return (
    <div className="space-y-6" data-testid="register-templates-page">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-8 h-8 text-blue-600" />
            Template Registri Presenze
          </h1>
          <p className="text-gray-600 mt-1">
            Gestisci i modelli .docx usati per generare i registri presenze. Carica nuovi template o imposta quello predefinito.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleOpenHelp} data-testid="placeholders-help-btn">
            <Info className="w-4 h-4 mr-2" /> Variabili supportate
          </Button>
          <Button variant="outline" onClick={async () => {
            if (!window.confirm('Ripristinare i template predefiniti eliminati? Verranno ricaricati i template builtin di default.')) return;
            try {
              const r = await api.post('/register-templates/restore-builtins');
              toast.success(`Ripristinati ${r.data?.builtin_count || 0} template predefiniti`);
              load();
            } catch (e) {
              toast.error('Errore: ' + (e.response?.data?.detail || e.message));
            }
          }} data-testid="restore-builtins-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Ripristina predefiniti
          </Button>
          <Button onClick={() => setShowUpload(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-template-btn">
            <Upload className="w-4 h-4 mr-2" /> Carica template
          </Button>
        </div>
      </motion.div>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="py-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Come funziona:</strong> i template <code>.docx</code> ora supportano i <strong>placeholder «...»</strong> come
            negli altri documenti (Incarico RSPP, Art. 18, ecc.): <code>«Codice_Progetto»</code>, <code>«Titolo_Corso»</code>,
            <code>«Docente_Nome»</code>, <code>«Data_Inizio»</code>, ecc. Inserisci una <strong>riga tabella</strong> con
            <code>«Discente_Cognome»</code>, <code>«Discente_Nome»</code>, <code>«Discente_CF»</code>, <code>«Discente_Firma»</code>:
            il sistema la replica automaticamente per ogni partecipante. Vedi "Variabili supportate".
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Caricamento...</div>
      ) : visibleTemplates.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-gray-500">
            <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
            Nessun template caricato. Clicca "Carica template" per iniziare.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Dimensione</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleTemplates.map((t) => (
                  <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {t.is_default && (
                          <Star className="w-4 h-4 fill-amber-400 text-amber-500 flex-shrink-0" />
                        )}
                        <span className="font-medium">{t.name}</span>
                        {t.is_builtin && (
                          <Badge variant="secondary" className="text-xs">
                            <ShieldCheck className="w-3 h-3 mr-1" /> Predefinito
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                      {t.description || '-'}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-gray-500 max-w-[200px] truncate">
                      {t.filename}
                    </TableCell>
                    <TableCell className="text-sm">{formatBytes(t.size_bytes)}</TableCell>
                    <TableCell>
                      {!t.file_exists ? (
                        <Badge variant="destructive">File mancante</Badge>
                      ) : t.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-800">Attivo</Badge>
                      ) : (
                        <Badge className="bg-gray-200 text-gray-700">Disattivato</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {!t.is_default && (
                          <Button
                            size="icon"
                            variant="ghost"
                            title="Imposta come predefinito"
                            onClick={() => setDefault(t)}
                            data-testid={`set-default-${t.id}`}
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Anteprima documento con placeholder evidenziati"
                          onClick={() => handlePreview(t)}
                          data-testid={`preview-template-${t.id}`}
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Verifica placeholder supportati nel template"
                          onClick={() => handleCheckPlaceholders(t)}
                          data-testid={`check-placeholders-${t.id}`}
                        >
                          <Code className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Scarica originale"
                          onClick={() => window.open(registerTemplatesApi.getDownloadUrl(t.id), '_blank')}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Rinomina"
                          onClick={() => setEditing({ id: t.id, name: t.name, description: t.description || '' })}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title={t.is_active ? 'Disattiva' : 'Riattiva'}
                          onClick={() => toggleActive(t)}
                        >
                          <Power className={`w-4 h-4 ${t.is_active ? 'text-emerald-600' : 'text-gray-400'}`} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title={t.is_builtin ? 'Elimina (predefinito — puoi ripristinarlo)' : 'Elimina'}
                          onClick={() => deleteTemplate(t)}
                          data-testid={`delete-template-${t.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Upload dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Carica nuovo template registro</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-1">
              <Label>File .docx *</Label>
              <Input
                ref={fileRef}
                type="file"
                accept=".docx"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                data-testid="upload-file-input"
                required
              />
              <p className="text-xs text-gray-500">
                Solo formato .docx (max 20 MB). Il template deve contenere tabelle con header
                COGNOME / NOME / CODICE FISCALE / LUOGO DI NASCITA / DATA DI NASCITA.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Nome template *</Label>
              <Input
                value={uploadForm.name}
                onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                placeholder="es. Formazione Specifica Rischio Basso"
                data-testid="upload-name-input"
                required
              />
            </div>
            <div className="space-y-1">
              <Label>Descrizione</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                rows={2}
                placeholder="Descrizione opzionale del template"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={uploadForm.is_default}
                onChange={(e) => setUploadForm({ ...uploadForm, is_default: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm">Imposta come template predefinito</span>
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={uploading} data-testid="confirm-upload-btn">
                {uploading ? 'Caricamento...' : 'Carica'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica template</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Nome *</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Descrizione</Label>
                <Textarea
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={renameTemplate}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog (docx-preview render con placeholder evidenziati) */}
      <Dialog open={!!previewDialog} onOpenChange={(o) => { if (!o) { setPreviewDialog(null); setPreviewValidation(null); setPreviewError(''); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Anteprima · {previewDialog?.template?.name}
            </DialogTitle>
          </DialogHeader>
          {previewValidation && (
            <div className="flex flex-wrap gap-2 text-xs border-b pb-3">
              <Badge className="bg-blue-100 text-blue-800">
                Placeholder trovati: {previewValidation.found?.length || 0}
              </Badge>
              {previewValidation.valid?.length > 0 && (
                <Badge className="bg-emerald-100 text-emerald-800">
                  ✓ Validi: {previewValidation.valid.length}
                </Badge>
              )}
              {previewValidation.unknown?.length > 0 && (
                <Badge className="bg-red-100 text-red-800">
                  ⚠ Sconosciuti: {previewValidation.unknown.length}
                </Badge>
              )}
              <span className="text-gray-500 italic">
                I placeholder «...» sono evidenziati in giallo nel documento
              </span>
            </div>
          )}
          <div
            className="flex-1 overflow-auto bg-gray-100 border rounded p-4"
            data-testid="register-template-preview-html"
          >
            {previewLoading && (
              <p className="text-gray-500 italic text-center py-6">Caricamento anteprima documento...</p>
            )}
            {previewError && !previewLoading && (
              <p className="text-red-600 text-center py-6">⚠ {previewError}</p>
            )}
            <div ref={previewContainerRef} className="docx-preview-host" />
          </div>
          <DialogFooter className="border-t pt-3">
            <Button
              variant="outline"
              onClick={() => previewDialog?.template && window.open(registerTemplatesApi.getDownloadUrl(previewDialog.template.id), '_blank')}
            >
              <Download className="w-4 h-4 mr-2" /> Scarica originale
            </Button>
            <Button variant="outline" onClick={() => { setPreviewDialog(null); setPreviewValidation(null); setPreviewError(''); }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Placeholder verification dialog */}
      <Dialog open={!!placeholderDialog} onOpenChange={(o) => !o && setPlaceholderDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Verifica placeholder · {placeholderDialog?.template?.name}</DialogTitle>
          </DialogHeader>
          {placeholderDialog && (
            <div className="space-y-4 text-sm">
              <div>
                <p className="font-semibold mb-1">Placeholder trovati nel template ({placeholderDialog.found?.length || 0})</p>
                {(placeholderDialog.found || []).length === 0 ? (
                  <p className="text-gray-500 italic">Nessun placeholder «...» trovato. Modifica il .docx in Word inserendoli (vedi "Variabili supportate").</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {placeholderDialog.found.map((v) => (
                      <Badge key={v} className={placeholderDialog.valid?.includes(v) ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                        «{v}»
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              {(placeholderDialog.unknown || []).length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="font-semibold text-red-800 mb-1">⚠ Placeholder NON supportati: questi non verranno sostituiti</p>
                  <div className="flex flex-wrap gap-1">
                    {placeholderDialog.unknown.map((v) => (
                      <code key={v} className="text-xs bg-white px-1 border rounded">«{v}»</code>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs text-gray-500">
                Suggerimento: i placeholder devono usare le virgolette francesi «» (in Word: <kbd>Inserisci → Simbolo</kbd>). In alternativa puoi usare anche <code>{'<<Variabile>>'}</code> (sostituiscili poi prima di caricare).
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlaceholderDialog(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help catalog dialog */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Variabili supportate nei Template Registri</DialogTitle>
          </DialogHeader>
          {catalog ? (
            <div className="space-y-4 text-sm">
              {(catalog.categories || []).map((cat) => (
                <div key={cat.label}>
                  <p className="font-semibold text-blue-900 mb-2">{cat.label}</p>
                  <div className="space-y-1">
                    {cat.items.map((it) => (
                      <div key={it.key} className="flex flex-wrap items-baseline gap-2 border-b pb-1">
                        <code
                          className="bg-blue-50 px-2 py-0.5 rounded font-mono text-xs cursor-pointer hover:bg-blue-100"
                          title="Clicca per copiare"
                          onClick={() => {
                            navigator.clipboard.writeText(`«${it.key}»`);
                            toast.success(`«${it.key}» copiato negli appunti`);
                          }}
                        >
                          «{it.key}»
                        </code>
                        <span className="text-gray-600">{it.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {catalog.note && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-900 text-xs">
                  💡 {catalog.note}
                </div>
              )}
            </div>
          ) : (
            <div className="py-10 text-center text-gray-500">Caricamento catalogo...</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHelp(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
